// Nine Lives '84 - the sleeping dogs room.
//
// Three dogs asleep on the floor, each with a bowl of milk in front of it.
// Crouch (hold down) at a bowl to drink it. A dog senses Tabs when he is
// close on the floor: its meter climbs, it growls at half, and at full it
// wakes and comes for him. Back off when you hear the growl; jump over
// them rather than walk past. A carton floats about the ceiling and now
// and then refills a bowl. Empty every bowl at once to finish.

import type { Cat } from '../cat';
import {
  BOWL_H,
  BOWL_W,
  DOG_H,
  DOG_SENSE,
  DOG_W,
  DT,
  GROWL_AT,
  K_DOWN,
  WORLD_W,
} from '../constants';
import { overlaps } from '../physics';
import { chaseDog, Room, type RoomContext, type RoomDog } from '../room';
import type { Box, GameEvent, RoomKind } from '../types';

export interface Bowl extends Box {
  /** 1 full .. 0 empty. */
  level: number;
}

export type DogState = 'asleep' | 'growl' | 'awake' | 'returning';

export interface SleepingDog extends RoomDog {
  state: DogState;
  /** 0 .. 1; wakes at 1. */
  meter: number;
  home: number;
  awakeTicks: number;
}

export type CartonState = 'float' | 'descend' | 'pour' | 'ascend';

export interface Carton extends Box {
  dir: 1 | -1;
  /** Ticks until the next refill while floating. */
  timer: number;
  state: CartonState;
  /** The bowl being refilled while descending or pouring. */
  target: Bowl | null;
  /** Ticks of pouring left. */
  pourTicks: number;
}

/** Where the carton floats between refills. */
export const CARTON_HOME_Y = 168;
/** How high above a bowl it hovers to pour. */
const POUR_HEIGHT = 10;
const CARTON_SPEED = 95;
const POUR_TICKS = 50;

const BOWL_XS = [70, 170, 262];

export class DogsRoom extends Room {
  readonly kind: RoomKind = 'dogs';
  readonly taskName = 'bowls';
  readonly bowls: Bowl[] = [];
  readonly dogs: SleepingDog[] = [];
  readonly carton: Carton;
  drinking = false;

  constructor(ctx: RoomContext) {
    super(ctx);
    for (const x of BOWL_XS) {
      this.bowls.push({ x, y: 0, w: BOWL_W, h: BOWL_H, level: 1 });
      const dx = x + BOWL_W + 4;
      this.dogs.push({
        x: dx,
        y: 0,
        w: DOG_W,
        h: DOG_H,
        dir: -1,
        state: 'asleep',
        meter: 0,
        home: dx,
        awakeTicks: 0,
      });
    }
    this.carton = {
      x: 140,
      y: CARTON_HOME_Y,
      w: 12,
      h: 18,
      dir: 1,
      timer: ctx.rng.int(...ctx.params.refillEvery),
      state: 'float',
      target: null,
      pourTicks: 0,
    };
  }

  get need(): number {
    return this.bowls.length;
  }

  get have(): number {
    return this.bowls.filter((b) => b.level <= 0).length;
  }

  /** The bowl Tabs is crouched at, if any. */
  bowlAt(cat: Cat): Bowl | null {
    if (!cat.onGround || cat.ground !== null) return null;
    return this.bowls.find((b) => b.level > 0 && overlaps(cat, b)) ?? null;
  }

  protected update(cat: Cat, keys: number, events: GameEvent[]): void {
    const p = this.ctx.params;
    const onFloor = cat.onGround && cat.ground === null && cat.knockedTicks === 0;

    const bowl = this.bowlAt(cat);
    this.drinking = !!bowl && !!(keys & K_DOWN);
    if (bowl && this.drinking) {
      bowl.level -= 1 / p.drinkTicks;
      if (this.ticks % 12 === 0) events.push({ kind: 'drink' });
      if (bowl.level <= 0) {
        bowl.level = 0;
        events.push({ kind: 'bowlEmpty', have: this.have, need: this.need });
        if (this.have >= this.need) {
          this.done = true;
          return;
        }
      }
    }

    const catCx = cat.x + cat.w / 2;
    for (const d of this.dogs) {
      switch (d.state) {
        case 'asleep':
        case 'growl': {
          const near = onFloor && Math.abs(catCx - (d.x + d.w / 2)) < DOG_SENSE;
          d.meter += near ? p.wakeRate * (this.drinking ? 1.6 : 1) : -p.wakeDecay;
          d.meter = Math.max(0, Math.min(1, d.meter));
          if (d.meter >= 1) {
            d.state = 'awake';
            d.awakeTicks = p.awakeTicks;
            events.push({ kind: 'dogWoke' });
          } else if (d.meter >= GROWL_AT && d.state === 'asleep') {
            d.state = 'growl';
            events.push({ kind: 'growl' });
          } else if (d.meter < GROWL_AT * 0.7 && d.state === 'growl') {
            d.state = 'asleep';
          }
          break;
        }
        case 'awake': {
          if (chaseDog(d, cat, p.dogSpeed * 0.6, p.dogCharge, 0, WORLD_W)) {
            this.fatal = 'dog';
            return;
          }
          if (--d.awakeTicks <= 0) d.state = 'returning';
          break;
        }
        case 'returning': {
          const dx = d.home - d.x;
          const step = p.dogSpeed * DT;
          if (Math.abs(dx) <= step) {
            d.x = d.home;
            d.state = 'asleep';
            d.meter = 0;
            d.dir = -1;
          } else {
            d.dir = dx > 0 ? 1 : -1;
            d.x += d.dir * step;
          }
          break;
        }
      }
    }

    this.updateCarton(events);
  }

  private updateCarton(events: GameEvent[]): void {
    const c = this.carton;
    const step = CARTON_SPEED * DT;
    switch (c.state) {
      case 'float': {
        c.x += c.dir * 40 * DT;
        if (c.x < 20) c.dir = 1;
        else if (c.x + c.w > WORLD_W - 20) c.dir = -1;
        if (--c.timer <= 0) {
          c.timer = this.ctx.rng.int(...this.ctx.params.refillEvery);
          const low = this.bowls.filter((b) => b.level < 1);
          if (low.length) {
            c.target = low[this.ctx.rng.int(0, low.length - 1)];
            c.state = 'descend';
          }
        }
        break;
      }
      case 'descend': {
        const b = c.target!;
        // Hover beside the bowl, tipped over it, low enough to be seen pouring.
        const tx = b.x + b.w / 2 - c.w - 2;
        const ty = b.y + b.h + POUR_HEIGHT;
        const dx = tx - c.x;
        const dy = ty - c.y;
        const dist = Math.hypot(dx, dy);
        if (dist <= step) {
          c.x = tx;
          c.y = ty;
          c.state = 'pour';
          c.pourTicks = POUR_TICKS;
          events.push({ kind: 'refill' });
        } else {
          c.x += (dx / dist) * step;
          c.y += (dy / dist) * step;
        }
        break;
      }
      case 'pour': {
        const b = c.target!;
        b.level = Math.min(1, b.level + 1 / POUR_TICKS);
        if (--c.pourTicks <= 0) {
          b.level = 1;
          c.state = 'ascend';
          c.target = null;
        }
        break;
      }
      case 'ascend': {
        c.y += step;
        if (c.y >= CARTON_HOME_Y) {
          c.y = CARTON_HOME_Y;
          c.state = 'float';
        }
        break;
      }
    }
  }
}
